import { useEffect } from 'react';
import type { RefObject } from 'react';

import type { CropperCanvas, CropperImage } from 'cropperjs';
import { debounce } from 'lodash-es';

import type { MediaCropArea } from '#src/app/shared/hooks/imageUpload';

import { getImageScale, translateToNaturalOffset } from '../coverTransform';

import { useClampToCover } from './useClampToCover';
import type { NaturalSize } from './useImageReady';

type Props = {
	canvasRef: RefObject<CropperCanvas | null>;
	imageRef: RefObject<CropperImage | null>;
	naturalSizeRef: RefObject<NaturalSize | undefined>;
	getCroppedCanvas: () => Promise<HTMLCanvasElement | undefined>;
	updateCrop: (imageCrop: MediaCropArea) => void;
	updateImage?: (blob: Blob) => void;
};

function getCropData(imageElement: CropperImage, naturalSize: NaturalSize, scale: number): MediaCropArea {
	const [, , , , translateX = 0, translateY = 0] = imageElement.$getTransform();
	const { width: naturalWidth, height: naturalHeight } = naturalSize;
	return {
		x: translateToNaturalOffset(translateX, naturalWidth, scale),
		y: translateToNaturalOffset(translateY, naturalHeight, scale),
		w: naturalWidth,
		h: naturalHeight,
		croppedWidth: naturalWidth * scale,
		croppedHeight: naturalHeight * scale,
	};
}

/**
 * On every image transform (drag/zoom/keyboard nudge), keeps the image covering the viewport and
 * reports the resulting crop area and cropped image.
 */
export function useCropOnTransform({
	canvasRef,
	imageRef,
	naturalSizeRef,
	getCroppedCanvas,
	updateCrop,
	updateImage,
}: Props): void {
	const clampToCover = useClampToCover({ canvasRef, imageRef, naturalSizeRef });

	// TODO: investigate why `onCrop` still gets called after its unmounted, leading to a potential
	// memory leak
	// To ensure we save the desired repositioned image, we update Uppy with the new cropped image from Cropper.
	// Testing library's drag and drop is not supported by jsdom
	/* istanbul ignore next */
	// eslint-disable-next-line react-hooks/refs
	const onCrop = debounce(async () => {
		const imageElement = imageRef.current;
		const scale = getImageScale(imageElement);
		const naturalSize = naturalSizeRef.current;
		if (!imageElement || !scale || !naturalSize) return;

		const cropData = getCropData(imageElement, naturalSize, scale);

		let croppedCanvas: HTMLCanvasElement | undefined;
		try {
			croppedCanvas = await getCroppedCanvas();
		} catch {
			// the element was disconnected from the DOM before the crop could be exported
			return;
		}

		if (!croppedCanvas) return;

		updateCrop({ ...cropData, newWidth: croppedCanvas.width, newHeight: croppedCanvas.height });

		try {
			croppedCanvas.toBlob((blob) => {
				if (blob && updateImage) updateImage(blob);
			}, 'image/jpeg');
		} catch {
			// canvas tainted by cross-origin image — crop cannot be exported
		}
	}, 250);

	// React doesn't wire up a plain `onTransform` JSX prop as a real `transform` event listener for
	// a custom element, so it's attached imperatively instead.
	useEffect(() => {
		const imageElement = imageRef.current;
		if (!imageElement) return undefined;
		const handleTransform = (event: Event) => {
			clampToCover(event);
			void onCrop();
		};
		imageElement.addEventListener('transform', handleTransform);
		return () => imageElement.removeEventListener('transform', handleTransform);
	});
}
